import { Dropdown, type DropdownProps } from 'primereact/dropdown';
import type {
	PaginatorPassThroughMethodOptions,
	PaginatorPassThroughOptions,
	PaginatorTemplateOptions,
} from 'primereact/paginator';
import { MdArrowDropDown } from 'react-icons/md';
import { cn } from '@/components/ui/cn';

export const PAGINATOR_CURRENT_REPORT =
	'Showing {first} to {last} of {totalRecords} entries';

const PAGINATOR_LAYOUT =
	'CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown';

const NAV_BUTTON = cn(
	'min-w-0 w-8 h-8 mx-0.5 inline-flex items-center justify-center rounded-full',
	'text-onSurfaceVariant transition-colors',
	'hover:bg-secondaryContainer hover:text-secondary',
	'disabled:opacity-40 disabled:pointer-events-none',
);

/**
 * Shared, branded paginator styling. Applied inline via `pt={{ paginator: paginatorPassThrough }}`
 * on each DataTable so there is no external stylesheet.
 */
export const paginatorPassThrough: PaginatorPassThroughOptions = {
	root: {
		className: cn(
			'flex items-center gap-1 m-0 py-2 px-3 border-t-0 rounded-b-lg',
			'bg-surfaceContainerLower font-poppins',
		),
	},
	current: {
		className: 'mr-auto text-caption text-onSurfaceVariant font-poppins',
	},
	pages: { className: 'inline-flex items-center' },
	firstPageButton: { className: NAV_BUTTON },
	prevPageButton: { className: NAV_BUTTON },
	nextPageButton: { className: NAV_BUTTON },
	lastPageButton: { className: NAV_BUTTON },
	pageButton: (options: PaginatorPassThroughMethodOptions) => ({
		className: cn(
			'min-w-0 w-8 h-8 mx-0.5 inline-flex items-center justify-center rounded-full',
			'text-body-small font-poppins transition-colors',
			options.context.active
				? 'bg-surfaceContainerHigh text-primary font-bold'
				: 'text-onSurfaceVariant hover:bg-secondaryContainer hover:text-secondary',
		),
	}),
};

/**
 * Shared, branded paginator template used by every table across the platform.
 *
 * Pass `onRowsChange` for lazy/server-side tables that need to reset the page
 * when the page size changes; omit it to fall back to PrimeReact's built-in
 * rows handling (which fires `onPage`).
 */
export const buildPaginatorTemplate = (
	onRowsChange?: (rows: number) => void,
): PaginatorTemplateOptions => ({
	layout: PAGINATOR_LAYOUT,
	RowsPerPageDropdown: (options: DropdownProps) => (
		<Dropdown
			unstyled
			value={options.value}
			options={options.options}
			onChange={(e) => {
				if (onRowsChange) {
					onRowsChange(e.value);
				} else {
					options.onChange?.(e);
				}
			}}
			appendTo={document.body}
			dropdownIcon={
				<MdArrowDropDown size={24} className="text-onSurfaceVariant" />
			}
			pt={{
				root: {
					className: cn(
						'inline-flex items-center justify-between gap-1',
						'h-9 pl-3 pr-1 rounded-md border border-outline bg-surface cursor-pointer',
					),
				},
				input: {
					className: 'flex items-center text-caption font-poppins text-onSurfaceVariant',
				},
				trigger: { className: 'flex items-center justify-center w-6' },
				panel: {
					className: cn(
						'py-1 rounded-md bg-surfaceContainer',
						'shadow-[0px_2px_6px_2px_#00000026,0px_1px_2px_0px_#0000004D]',
					),
				},
				item: {
					className: cn(
						'px-4 py-2 cursor-pointer focus-visible:outline-none',
						'hover:bg-[#D8DDE0] data-[p-highlight=true]:bg-[#D8DDE0]',
					),
				},
				itemLabel: { className: 'font-poppins text-body-small text-onSurface' },
			}}
		/>
	),
});
